/*
 * Project: mftel.net
 * Company: Matrix Family Inc. (https://matrix.family)
 * Owner: Matrix Family Inc.
 * Contact: dev@matrix.family
 * Support: support@matrix.family
 * Matrix: @support:matrix.family
 * Date: Thu, 23 Jul 2026 03:15:00 UTC
 * Status: Created
 */

import { create } from 'zustand/react';

const STORAGE_KEY = 'mf_web_settings';
const LEGACY_STORAGE_KEY = 'settings';

export enum MessageLayout {
  Modern = 0,
  Compact = 1,
  Bubble = 2,
}

export type ThemeMode = 'dark' | 'light' | 'system';

export interface Settings {
  monochromeMode?: boolean;
  themeMode: ThemeMode;
  pageZoom: number;
  hideActivity: boolean;

  memberSortIndex: number;
  messageLayout: MessageLayout;
  hideMembershipUpdates: boolean;
  hideProfileUpdates: boolean;
  showHiddenEvents: boolean;
  classicUsernameColors: boolean;

  showNotifications: boolean;
  notificationSounds: boolean;
  notificationPreview: boolean;

  use24HourClock: boolean;
  dateFormat: string;

  // General settings
  sendOnEnter: boolean;
  showTypingIndicator: boolean;
  showReadReceipts: boolean;

  // Privacy
  sharePresence: boolean;
}

const defaultSettings: Settings = {
  monochromeMode: false,
  themeMode: 'dark',
  pageZoom: 100,
  hideActivity: false,

  memberSortIndex: 0,
  messageLayout: 0,
  hideMembershipUpdates: false,
  hideProfileUpdates: true,
  showHiddenEvents: false,
  classicUsernameColors: false,

  showNotifications: true,
  notificationSounds: true,
  notificationPreview: true,

  use24HourClock: false,
  dateFormat: 'D MMM YYYY',

  // General settings
  sendOnEnter: true,
  showTypingIndicator: true,
  showReadReceipts: true,

  // Privacy
  sharePresence: true,
};

/** Old stored field names mapped to the current schema. */
const LEGACY_FIELD_MAP: Record<string, keyof Settings> = {
  memberSortFilterIndex: 'memberSortIndex',
  hideMembershipEvents: 'hideMembershipUpdates',
  hideNickAvatarEvents: 'hideProfileUpdates',
  legacyUsernameColor: 'classicUsernameColors',
  isNotificationSounds: 'notificationSounds',
  hour24Clock: 'use24HourClock',
  dateFormatString: 'dateFormat',
};

const parseRecord = (raw: string): Record<string, unknown> | null => {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed as Record<string, unknown>;
  } catch {
    // corrupted record - fall back to defaults
  }
  return null;
};

/** Read stored settings, migrating the legacy record once if present. */
const readStoredSettings = (): Partial<Settings> | null => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored !== null) return parseRecord(stored) as Partial<Settings> | null;

  const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
  if (legacy === null) return null;

  const parsed = parseRecord(legacy);
  if (!parsed) {
    localStorage.removeItem(LEGACY_STORAGE_KEY);
    return null;
  }

  const migrated: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(parsed)) {
    const targetKey = LEGACY_FIELD_MAP[key] ?? key;
    if (targetKey in defaultSettings) migrated[targetKey] = value;
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(migrated));
  localStorage.removeItem(LEGACY_STORAGE_KEY);
  return migrated as Partial<Settings>;
};

export const getSettings = (): Settings => {
  const stored = readStoredSettings();
  if (!stored) return defaultSettings;
  return {
    ...defaultSettings,
    ...stored,
  };
};

export const setSettings = (settings: Settings) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
};

type SettingsStore = {
  settings: Settings;
  updateSettings: (settings: Settings) => void;
};

export const useSettingsStore = create<SettingsStore>()((set) => ({
  settings: getSettings(),
  updateSettings: (settings) => {
    setSettings(settings);
    set({ settings });
  },
}));
